import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";

/**
 * Take payment with a customer's gift card: look up the code they show,
 * check the balance, then take the amount off it.
 */
export default function GiftCards() {
  const [code, setCode] = useState("");
  const [card, setCard] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [settlement, setSettlement] = useState(null);

  const loadRedemptions = () => {
    api
      .get("/owner/gift-cards/redemptions")
      .then(({ data }) => setRedemptions(data))
      .catch(() => setRedemptions([]));
    api
      .get("/owner/gift-cards/settlement")
      .then(({ data }) => setSettlement(data))
      .catch(() => setSettlement(null));
  };

  useEffect(() => {
    loadRedemptions();
  }, []);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(e?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const lookup = (e) => {
    e.preventDefault();
    setCard(null);
    run(async () => {
      const { data } = await api.post("/owner/gift-cards/lookup", { code });
      setCard(data);
      setAmount(String(Number(data.remaining_amount)));
    });
  };

  const redeem = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/owner/gift-cards/redeem", { code: card.code, amount: Number(amount) });
      setNotice(`Took ${formatPrice(amount)} off gift card ${data.code}. Remaining balance: ${formatPrice(data.remaining_amount)}.`);
      setCard(null);
      setCode("");
      setAmount("");
      loadRedemptions();
    });
  };

  return (
    <div>
      <h1 className="font-montserrat text-2xl font-bold text-slate-900">Gift cards</h1>
      <p className="mb-4 mt-1 text-sm text-muted-foreground">
        When a customer pays with a gift card, enter the code they show you to check the balance and take the payment.
      </p>

      <Card className="max-w-lg p-4">
        <form onSubmit={lookup} className="flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="gift-card-code" className="mb-1 block text-xs text-muted-foreground">
              Gift card code
            </label>
            <Input
              id="gift-card-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setCard(null);
              }}
              placeholder="e.g. 7KQ2M9XA4B"
              autoComplete="off"
              className="font-mono"
            />
          </div>
          <Button type="submit" variant="outline" disabled={busy || !code.trim()}>
            Check balance
          </Button>
        </form>

        {card && (
          <form onSubmit={redeem} className="mt-4 border-t border-border pt-4">
            <p className="text-sm text-muted-foreground">Available balance</p>
            <p className="text-2xl font-bold text-slate-900">{formatPrice(card.remaining_amount)}</p>
            {card.expires_at && (
              <p className="text-xs text-muted-foreground">Expires {new Date(card.expires_at).toLocaleDateString()}</p>
            )}
            <div className="mt-3 flex items-end gap-2">
              <div className="flex-1">
                <label htmlFor="gift-card-amount" className="mb-1 block text-xs text-muted-foreground">
                  Amount to take (RWF)
                </label>
                <Input
                  id="gift-card-amount"
                  type="number"
                  min={1}
                  max={Number(card.remaining_amount)}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !(Number(amount) > 0) || Number(amount) > Number(card.remaining_amount)}
              >
                Redeem
              </Button>
            </div>
          </form>
        )}

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        {notice && <p className="mt-3 rounded-xl bg-accent p-3 text-sm text-accent-foreground">{notice}</p>}
      </Card>

      {settlement && (settlement.owed > 0 || settlement.in_payout > 0 || settlement.payouts.length > 0) && (
        <SettlementSummary settlement={settlement} />
      )}

      <h2 className="mb-3 mt-8 font-montserrat text-lg font-bold text-slate-900">Recent redemptions</h2>
      {redemptions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No gift cards redeemed yet.</p>
      ) : (
        <div className="max-w-lg divide-y divide-border rounded-2xl border border-border">
          {redemptions.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 p-3 text-sm">
              <div>
                <p className="font-mono text-slate-900">{r.gift_card?.code}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                  {r.redeemer?.full_name && ` · ${r.redeemer.full_name}`}
                  {r.settlement_due && " · SHUWA card, paid out by SHUWA"}
                </p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-slate-900">−{formatPrice(r.amount)}</p>
                <p className="text-xs text-muted-foreground">{formatPrice(r.balance_after)} left</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const METHOD_LABEL = { bank_transfer: "Bank transfer", mtn: "MTN MoMo", airtel: "Airtel Money", cash: "Cash", other: "Other" };

/**
 * Gift cards valid at any business are paid to SHUWA, so SHUWA pays this
 * business back for the ones it accepted.
 */
function SettlementSummary({ settlement }) {
  return (
    <div className="mt-8 max-w-lg">
      <h2 className="font-montserrat text-lg font-bold text-slate-900">SHUWA gift card payouts</h2>
      <p className="mb-3 mt-1 text-sm text-muted-foreground">
        Gift cards valid at any business are paid to SHUWA. SHUWA pays you for the ones you accept.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Owed to you", value: settlement.owed },
          { label: "Being paid", value: settlement.in_payout },
          { label: "Paid so far", value: settlement.paid_total },
        ].map((s) => (
          <Card key={s.label} className="p-3">
            <p className="text-xs text-muted-foreground">{s.label}</p>
            <p className="font-bold text-slate-900">{formatPrice(s.value)}</p>
          </Card>
        ))}
      </div>
      {settlement.payouts.length > 0 && (
        <div className="mt-3 divide-y divide-border rounded-2xl border border-border">
          {settlement.payouts.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-3 p-3 text-sm">
              <div>
                <p className="font-semibold text-slate-900">{formatPrice(p.amount)}</p>
                <p className="text-xs text-muted-foreground">
                  {p.status === "paid"
                    ? `Paid ${new Date(p.paid_at).toLocaleDateString()} · ${METHOD_LABEL[p.method] || p.method}${p.reference ? ` · ref ${p.reference}` : ""}`
                    : "Being sent"}
                </p>
              </div>
              <Badge variant={p.status === "paid" ? "accent" : "primary"}>{p.status === "paid" ? "paid" : "pending"}</Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
